import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users2, BookOpen, Zap, Mic, Target, TrendingUp, Rocket, Briefcase,
  Check, ArrowRight, Star, Crown, Sparkles, Calendar, MessageSquare,
  FileText, BarChart3, Phone, Video, Clock, Shield, Award, Gift
} from "lucide-react";

const packages = [
  {
    id: "community",
    name: "Community Membership",
    tagline: "Connect & Learn",
    price: "Free",
    priceNote: "Always free",
    icon: Users2,
    color: "emerald",
    popular: false,
    description: "Join our thriving community of entrepreneurs and access valuable resources.",
    features: [
      "Access to Community Hub",
      "View community posts & discussions",
      "Participate in group conversations",
      "Bulletin board announcements",
      "Free workshop registration",
      "Community member badge",
      "Networking opportunities",
      "Help & Docs resources"
    ],
    cta: "Join Free",
    ctaLink: "/register"
  },
  {
    id: "basic",
    name: "Basic",
    tagline: "Get Started",
    price: "$250",
    priceNote: "per month",
    icon: BookOpen,
    color: "blue",
    popular: false,
    description: "Essential tools and support to build your business foundation.",
    features: [
      "Everything in Community, plus:",
      "Personal client dashboard",
      "Weekly progress tracking & logs",
      "Vision & goals planning tools",
      "Ideal client profile builder",
      "Monthly 1:1 session (30 min)",
      "Email support",
      "Business templates library",
      "Progress analytics & insights",
      "Referral rewards program"
    ],
    cta: "Get Started",
    ctaLink: "/pricing"
  },
  {
    id: "rapid",
    name: "R.A.P.I.D.™",
    tagline: "Business Recalibration",
    price: "$1,250",
    priceNote: "one-time investment",
    icon: Zap,
    color: "amber",
    popular: true,
    description: "Intensive 5-phase business transformation program for rapid results.",
    features: [
      "Review: Complete business audit",
      "Analyze: Market & competitive analysis",
      "Plan: Strategic roadmap development",
      "Implement: Guided execution support",
      "Develop: Systems optimization",
      "5 dedicated strategy sessions",
      "Custom action plans per phase",
      "Resource library & worksheets",
      "Priority email support",
      "90-day implementation timeline"
    ],
    cta: "Transform Now",
    ctaLink: "/pricing"
  },
  {
    id: "podcast",
    name: "Podcast Academy",
    tagline: "Launch Your Voice",
    price: "$500",
    priceNote: "one-time investment",
    icon: Mic,
    color: "purple",
    popular: false,
    description: "12-week comprehensive program to launch and grow your podcast.",
    features: [
      "Week-by-week structured curriculum",
      "Equipment & tech setup guidance",
      "Content planning & frameworks",
      "Recording & editing tutorials",
      "Distribution & platform setup",
      "Growth strategies & marketing",
      "Monetization blueprint",
      "Guest booking templates",
      "Weekly group coaching calls",
      "Launch support & accountability"
    ],
    cta: "Start Podcasting",
    ctaLink: "/pricing"
  },
  {
    id: "strategic",
    name: "Strategic Foundation Retainer",
    tagline: "Build & Position",
    price: "$750",
    priceNote: "3-month engagement",
    icon: Target,
    color: "cyan",
    popular: false,
    description: "Comprehensive strategy support to position your business for growth.",
    features: [
      "Everything in Basic, plus:",
      "Bi-weekly 1:1 sessions (45 min)",
      "StoryBrand messaging framework",
      "Brand positioning workshop",
      "Marketing strategy development",
      "Content calendar planning",
      "Sales funnel optimization",
      "KPI tracking & reporting",
      "Quarterly business reviews",
      "Slack/messaging access"
    ],
    cta: "Build Foundation",
    ctaLink: "/pricing"
  },
  {
    id: "growth",
    name: "Growth & Optimization Retainer",
    tagline: "Scale Smart",
    price: "$2,500",
    priceNote: "6-month engagement",
    icon: TrendingUp,
    color: "green",
    popular: true,
    description: "Accelerate your growth with advanced strategies and dedicated support.",
    features: [
      "Everything in Strategic, plus:",
      "Weekly 1:1 sessions (45 min)",
      "Revenue optimization strategies",
      "Team building & delegation",
      "Operations systemization",
      "Financial planning guidance",
      "Advanced marketing campaigns",
      "Partnership strategy",
      "Monthly performance deep-dives",
      "On-demand business hours support"
    ],
    cta: "Accelerate Growth",
    ctaLink: "/pricing"
  },
  {
    id: "scale",
    name: "Scale & Leadership Retainer",
    tagline: "Executive Excellence",
    price: "$5,000",
    priceNote: "12-month engagement",
    icon: Rocket,
    color: "rose",
    popular: false,
    premium: true,
    description: "White-glove support for established businesses ready to scale.",
    features: [
      "Everything in Growth, plus:",
      "Unlimited 1:1 access",
      "Executive leadership coaching",
      "Board/advisory development",
      "Exit strategy planning",
      "Investment readiness prep",
      "High-level networking intros",
      "VIP event access",
      "Direct phone/text access",
      "Same-day response guarantee",
      "Annual strategic retreat"
    ],
    cta: "Go Premium",
    ctaLink: "/pricing"
  },
  {
    id: "project",
    name: "Project-Based",
    tagline: "Custom Solutions",
    price: "$600",
    priceNote: "per project",
    icon: Briefcase,
    color: "slate",
    popular: false,
    description: "Focused consulting for specific business needs and deliverables.",
    features: [
      "Scoped project deliverables",
      "Defined timeline & milestones",
      "Website/brand refresh",
      "Launch campaigns",
      "System implementations",
      "Team training programs",
      "Process documentation",
      "Project management dashboard",
      "Revision rounds included",
      "Final delivery & handoff"
    ],
    cta: "Start Project",
    ctaLink: "/pricing"
  }
];

const colorClasses = {
  emerald: { bg: "bg-emerald-500", light: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600" },
  blue: { bg: "bg-blue-500", light: "bg-blue-50", border: "border-blue-200", text: "text-blue-600" },
  amber: { bg: "bg-amber-500", light: "bg-amber-50", border: "border-amber-200", text: "text-amber-600" },
  purple: { bg: "bg-purple-500", light: "bg-purple-50", border: "border-purple-200", text: "text-purple-600" },
  cyan: { bg: "bg-cyan-500", light: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-600" },
  green: { bg: "bg-green-500", light: "bg-green-50", border: "border-green-200", text: "text-green-600" },
  rose: { bg: "bg-rose-500", light: "bg-rose-50", border: "border-rose-200", text: "text-rose-600" },
  slate: { bg: "bg-slate-500", light: "bg-slate-50", border: "border-slate-200", text: "text-slate-600" }
};

const PackageCard = ({ pkg, expanded = false }) => {
  const colors = colorClasses[pkg.color];
  const Icon = pkg.icon;
  
  return (
    <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
      pkg.popular ? 'ring-2 ring-[#39FF14] shadow-lg' : ''
    } ${pkg.premium ? 'bg-gradient-to-br from-neutral-900 to-neutral-800 text-white' : ''}`}>
      {pkg.popular && (
        <div className="absolute top-4 right-4">
          <Badge className="bg-[#39FF14] text-white">
            <Star className="w-3 h-3 mr-1" />
            Most Popular
          </Badge>
        </div>
      )}
      {pkg.premium && (
        <div className="absolute top-4 right-4">
          <Badge className="bg-gradient-to-r from-amber-400 to-amber-600 text-black">
            <Crown className="w-3 h-3 mr-1" />
            Premium
          </Badge>
        </div>
      )}
      
      <CardHeader className="pb-4">
        <div className={`w-14 h-14 rounded-2xl ${pkg.premium ? 'bg-white/10' : colors.light} flex items-center justify-center mb-4`}>
          <Icon className={`w-7 h-7 ${pkg.premium ? 'text-white' : colors.text}`} />
        </div>
        <div className="space-y-1">
          <CardTitle className={`text-xl ${pkg.premium ? 'text-white' : 'text-neutral-900'}`}>
            {pkg.name}
          </CardTitle>
          <CardDescription className={pkg.premium ? 'text-neutral-300' : 'text-neutral-500'}>
            {pkg.tagline}
          </CardDescription>
        </div>
        <div className="pt-4">
          <span className={`text-4xl font-bold ${pkg.premium ? 'text-white' : 'text-neutral-900'}`}>
            {pkg.price}
          </span>
          <span className={`text-sm ml-2 ${pkg.premium ? 'text-neutral-400' : 'text-neutral-500'}`}>
            {pkg.priceNote}
          </span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <p className={`text-sm ${pkg.premium ? 'text-neutral-300' : 'text-neutral-600'}`}>
          {pkg.description}
        </p>
        
        <div className="space-y-3">
          {pkg.features.map((feature, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full ${pkg.premium ? 'bg-white/20' : colors.light} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Check className={`w-3 h-3 ${pkg.premium ? 'text-white' : colors.text}`} />
              </div>
              <span className={`text-sm ${pkg.premium ? 'text-neutral-200' : 'text-neutral-700'}`}>
                {feature}
              </span>
            </div>
          ))}
        </div>
        
        <Link to={pkg.ctaLink} className="block">
          <Button 
            className={`w-full ${
              pkg.popular 
                ? 'bg-[#39FF14] hover:bg-[#009900] text-white' 
                : pkg.premium
                  ? 'bg-white text-neutral-900 hover:bg-neutral-100'
                  : 'bg-neutral-900 hover:bg-neutral-800 text-white'
            }`}
            size="lg"
          >
            {pkg.cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

const ComparisonTable = () => {
  const features = [
    { name: "Community Hub Access", community: true, basic: true, rapid: true, podcast: true, strategic: true, growth: true, scale: true },
    { name: "Client Dashboard", community: false, basic: true, rapid: true, podcast: false, strategic: true, growth: true, scale: true },
    { name: "Weekly Progress Tracking", community: false, basic: true, rapid: false, podcast: false, strategic: true, growth: true, scale: true },
    { name: "1:1 Strategy Sessions", community: false, basic: "Monthly", rapid: "5 Sessions", podcast: false, strategic: "Bi-weekly", growth: "Weekly", scale: "Unlimited" },
    { name: "Email Support", community: false, basic: true, rapid: "Priority", podcast: true, strategic: true, growth: true, scale: true },
    { name: "Slack/Messaging Access", community: false, basic: false, rapid: false, podcast: false, strategic: true, growth: true, scale: true },
    { name: "Phone/Text Access", community: false, basic: false, rapid: false, podcast: false, strategic: false, growth: false, scale: true },
    { name: "StoryBrand Framework", community: false, basic: false, rapid: false, podcast: false, strategic: true, growth: true, scale: true },
    { name: "Marketing Strategy", community: false, basic: false, rapid: true, podcast: true, strategic: true, growth: "Advanced", scale: "Advanced" },
    { name: "Business Templates", community: false, basic: true, rapid: true, podcast: true, strategic: true, growth: true, scale: true },
    { name: "Quarterly Reviews", community: false, basic: false, rapid: false, podcast: false, strategic: true, growth: true, scale: true },
    { name: "VIP Event Access", community: false, basic: false, rapid: false, podcast: false, strategic: false, growth: false, scale: true },
  ];

  const renderCell = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-green-500 mx-auto" />;
    if (value === false) return <span className="text-neutral-300">—</span>;
    return <span className="text-sm font-medium text-neutral-700">{value}</span>;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-neutral-200">
            <th className="text-left py-4 px-4 font-semibold text-neutral-900">Features</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900">Community</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900">Basic</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900 bg-amber-50">R.A.P.I.D.™</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900">Podcast</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900">Strategic</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900 bg-green-50">Growth</th>
            <th className="text-center py-4 px-3 font-semibold text-neutral-900">Scale</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature, idx) => (
            <tr key={idx} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="py-3 px-4 text-sm text-neutral-700">{feature.name}</td>
              <td className="py-3 px-3 text-center">{renderCell(feature.community)}</td>
              <td className="py-3 px-3 text-center">{renderCell(feature.basic)}</td>
              <td className="py-3 px-3 text-center bg-amber-50/50">{renderCell(feature.rapid)}</td>
              <td className="py-3 px-3 text-center">{renderCell(feature.podcast)}</td>
              <td className="py-3 px-3 text-center">{renderCell(feature.strategic)}</td>
              <td className="py-3 px-3 text-center bg-green-50/50">{renderCell(feature.growth)}</td>
              <td className="py-3 px-3 text-center">{renderCell(feature.scale)}</td>
            </tr>
          ))}
          <tr className="bg-neutral-50 font-semibold">
            <td className="py-4 px-4 text-neutral-900">Price</td>
            <td className="py-4 px-3 text-center text-neutral-900">Free</td>
            <td className="py-4 px-3 text-center text-neutral-900">$250/mo</td>
            <td className="py-4 px-3 text-center text-amber-600 bg-amber-50">$1,250</td>
            <td className="py-4 px-3 text-center text-neutral-900">$500</td>
            <td className="py-4 px-3 text-center text-neutral-900">$500/mo</td>
            <td className="py-4 px-3 text-center text-green-600 bg-green-50">$750/mo</td>
            <td className="py-4 px-3 text-center text-neutral-900">$1,000/mo</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export const PackagesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");

  const subscriptions = packages.filter(p => ["basic", "strategic", "growth", "scale"].includes(p.id));
  const programs = packages.filter(p => ["rapid", "podcast"].includes(p.id));
  const other = packages.filter(p => ["community", "project"].includes(p.id));

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white" data-testid="packages-page">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-[#39FF14] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">MB</span>
            </div>
            <span className="font-semibold text-neutral-900">My Biz Consulting</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/pricing">
              <Button variant="outline">View Pricing</Button>
            </Link>
            <Link to="/login">
              <Button className="bg-[#39FF14] hover:bg-[#009900] text-white">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-[#39FF14]/10 text-[#39FF14] mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Packages & Services
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
            Choose Your Path to
            <span className="text-[#39FF14]"> Business Success</span>
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            From community access to executive coaching, we have the perfect package 
            to help you achieve your business goals at every stage of growth.
          </p>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-neutral-100">
                <TabsTrigger value="all" className="data-[state=active]:bg-white">
                  All Packages
                </TabsTrigger>
                <TabsTrigger value="subscriptions" className="data-[state=active]:bg-white">
                  Monthly Plans
                </TabsTrigger>
                <TabsTrigger value="programs" className="data-[state=active]:bg-white">
                  Programs
                </TabsTrigger>
                <TabsTrigger value="compare" className="data-[state=active]:bg-white">
                  Compare All
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="all">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {packages.map((pkg) => (
                  <PackageCard key={pkg.id} pkg={pkg} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="subscriptions">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">Monthly Subscription Plans</h2>
                  <p className="text-neutral-600">Ongoing support and coaching for continuous growth</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {subscriptions.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} />
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="programs">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">Specialized Programs</h2>
                  <p className="text-neutral-600">Intensive programs for specific business transformations</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {programs.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} />
                  ))}
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {other.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} />
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="compare">
              <div className="bg-white rounded-xl border border-neutral-200 p-6 overflow-hidden">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">Compare All Packages</h2>
                  <p className="text-neutral-600">See what's included in each tier</p>
                </div>
                <ComparisonTable />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-neutral-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Not Sure Which Package is Right for You?
          </h2>
          <p className="text-neutral-400 mb-8 max-w-2xl mx-auto">
            Book a free discovery call and we'll help you find the perfect fit 
            for your business goals and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-[#39FF14] hover:bg-[#009900] text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Book Discovery Call
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-neutral-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              { q: "Can I upgrade or downgrade my plan?", a: "Yes! You can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the next billing cycle." },
              { q: "What happens after I complete R.A.P.I.D.™?", a: "After completing the program, you can transition to any of our monthly subscription plans to continue your growth journey with ongoing support." },
              { q: "Do you offer refunds?", a: "We offer a 14-day satisfaction guarantee on monthly subscriptions. Programs are non-refundable once started, but we're committed to your success." },
              { q: "Can I pause my subscription?", a: "Yes, you can pause your subscription for up to 2 months per year if needed. Contact us to arrange this." }
            ].map((faq, idx) => (
              <Card key={idx} className="border-neutral-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-neutral-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-neutral-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-100 py-8 px-4 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#39FF14] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">MB</span>
            </div>
            <span className="text-sm text-neutral-600">© 2026 My Biz Consulting. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-neutral-600">
            <Link to="/pricing" className="hover:text-neutral-900">Pricing</Link>
            <Link to="/login" className="hover:text-neutral-900">Login</Link>
            <Link to="/register" className="hover:text-neutral-900">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PackagesPage;
